import { useState } from "react";
import { useQuery, startTradovateOAuth } from "wasp/client/operations";
import { getPropFirmTemplates } from "wasp/client/operations";
import { Building2, Loader2, ArrowLeft } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../client/components/ui/dialog";
import { Button } from "../../client/components/ui/button";
import { cn } from "../../client/utils";
import useTranslation from "../../client/hooks/useTranslation";
import { useToast } from "../../client/hooks/use-toast";
import type { PropFirmTemplateSnapshot } from "../propFirmEngine";
import { filterTradovatePropFirms } from "./tradovatePropFirms";

interface Props {
  open: boolean;
  onClose: () => void;
  // When set, the wizard runs in "add account to existing connection" mode:
  // it only asks for the prop firm (step 1) and then opens the import review
  // on the existing connection — no new OAuth, no connection-limit check
  // (Tradovate is 1 connection → N accounts).
  existingConnectionId?: string;
  onAddAccount?: (propFirmTemplateId: string | null) => void;
}

export default function TradovateConnectWizard({
  open,
  onClose,
  existingConnectionId,
  onAddAccount,
}: Props) {
  const t = useTranslation();
  const { toast } = useToast();

  const { data: templates = [] } = useQuery(getPropFirmTemplates);

  const [step, setStep] = useState<1 | 2>(1);
  const [propFirmTemplateId, setPropFirmTemplateId] = useState<string | null>(null);
  const [environment, setEnvironment] = useState<"live" | "demo">("live");
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setStep(1);
    setPropFirmTemplateId(null);
    setEnvironment("live");
    setLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handlePickPropFirm = (id: string | null) => {
    // Add-account mode: skip OAuth entirely, open the import review on the
    // existing connection with the chosen prop firm (for tier detection).
    if (existingConnectionId) {
      onAddAccount?.(id);
      reset();
      onClose();
      return;
    }
    setPropFirmTemplateId(id);
    setStep(2);
  };

  const handleContinue = async () => {
    setLoading(true);
    try {
      const result = await startTradovateOAuth({ propFirmTemplateId, environment });
      window.location.href = result.url;
    } catch (err: any) {
      const msg = err?.message ?? "";
      if (msg.includes("403") || msg.toLowerCase().includes("limit")) {
        toast({ title: t.tradovate.errorLimitReached, variant: "destructive" });
      } else {
        toast({
          title: t.tradovate.errorConnectionFailed,
          description: msg || undefined,
          variant: "destructive",
        });
      }
      setLoading(false);
    }
  };

  // Topstep et Bulenox ne passent pas par Tradovate : les afficher ici
  // promettrait une synchronisation impossible.
  const propFirmTemplates = filterTradovatePropFirms(templates as PropFirmTemplateSnapshot[]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {existingConnectionId ? t.tradovate.addAccountTitle : t.tradovate.wizardTitle}
          </DialogTitle>
          {step === 1 && (
            <DialogDescription>{t.tradovate.pickPropFirmHint}</DialogDescription>
          )}
        </DialogHeader>

        {step === 1 && (
          <div className="mt-2 space-y-3">
            <p className="text-sm font-medium">{t.tradovate.pickPropFirm}</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {propFirmTemplates.map((tpl) => (
                <button
                  key={tpl.id}
                  type="button"
                  onClick={() => handlePickPropFirm(tpl.id)}
                  className={cn(
                    "flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2.5 text-left text-sm transition-colors hover:border-primary/60 hover:bg-primary/5",
                  )}
                >
                  <span
                    className="inline-block h-3 w-3 shrink-0 rounded-full"
                    style={{ backgroundColor: tpl.color }}
                  />
                  <span className="truncate">{tpl.name}</span>
                </button>
              ))}
              <button
                type="button"
                onClick={() => handlePickPropFirm(null)}
                className="flex items-center gap-2 rounded-md border border-dashed border-border bg-transparent px-3 py-2.5 text-left text-sm text-muted-foreground transition-colors hover:border-primary/60 hover:bg-primary/5"
              >
                <Building2 className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{t.tradovate.otherPropFirm}</span>
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="mt-2 space-y-4">
            <div className="space-y-2">
              <p className="text-sm font-medium">{t.tradovate.environment}</p>
              <div className="grid grid-cols-2 gap-2">
                {(["live", "demo"] as const).map((env) => (
                  <button
                    key={env}
                    type="button"
                    onClick={() => setEnvironment(env)}
                    className={cn(
                      "rounded-md border px-3 py-2.5 text-sm font-medium transition-colors",
                      environment === env
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-card text-foreground hover:border-primary/40",
                    )}
                  >
                    {env === "live" ? t.tradovate.envLive : t.tradovate.envDemo}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setStep(1)}
                disabled={loading}
              >
                <ArrowLeft className="mr-1 h-3.5 w-3.5" />
                {t.tradovate.pickPropFirm}
              </Button>
              <Button type="button" onClick={handleContinue} disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t.tradovate.continueToTradovate}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
